/**
 * FreeShippingBar — <free-shipping-bar> custom element
 *
 * Listens to the Shopify PubSub `cart-update` event (published by
 * cart.js, cart-drawer.js, product-form.js) and re-renders the
 * progress bar without any page reload.
 *
 * Data attributes (set by free-shipping-bar.liquid):
 *   data-threshold        — free shipping threshold in cents (integer)
 *   data-remaining-text   — template string with [amount] placeholder
 *   data-success-text     — string shown when threshold is reached
 *
 * Dependencies:
 *   - pubsub.js    (subscribe / PUB_SUB_EVENTS)
 *   - global.js    (formatMoney — optional; falls back to Intl)
 */

if (!customElements.get('free-shipping-bar')) {
  customElements.define(
    'free-shipping-bar',
    class FreeShippingBar extends HTMLElement {
      /* ── Lifecycle ─────────────────────────────────────── */

      connectedCallback() {
        this._unsubscribe = subscribe(
          PUB_SUB_EVENTS.cartUpdate,
          this._onCartUpdate.bind(this)
        );
      }

      disconnectedCallback() {
        if (this._unsubscribe) this._unsubscribe();
      }

      /* ── Cart update handler ───────────────────────────── */

      _onCartUpdate(event) {
        // If the event carries the full cart payload, use it directly
        // to avoid an extra fetch (saves a round-trip on every add).
        const cartData = event?.cartData;

        if (cartData && typeof cartData.total_price === 'number') {
          this._render(cartData.total_price);
        } else {
          // Fallback: fetch fresh cart data
          fetch(`${window.routes?.cart_url ?? '/cart'}.js`, {
            credentials: 'same-origin',
          })
            .then((r) => r.json())
            .then((cart) => this._render(cart.total_price))
            .catch(() => {});
        }
      }

      /* ── Render ────────────────────────────────────────── */

      _render(totalPriceCents) {
        const thresholdCents = parseInt(this.dataset.threshold, 10);
        if (!thresholdCents || thresholdCents <= 0) return;

        const remainingCents = Math.max(thresholdCents - totalPriceCents, 0);
        const progress = Math.min(
          (totalPriceCents / thresholdCents) * 100,
          100
        );
        const unlocked = remainingCents <= 0;

        /* ── Bar fill ──────────────────────────────────── */
        const fill = this.querySelector('.free-ship-bar__fill');
        if (fill) fill.style.width = `${progress}%`;

        /* ── Text ─────────────────────────────────────── */
        const msgEl = this.querySelector('.free-ship-bar__msg');
        if (msgEl) {
          if (unlocked) {
            msgEl.className = 'free-ship-bar__msg free-ship-bar__msg--success';
            msgEl.textContent = this.dataset.successText || '';
          } else {
            msgEl.className =
              'free-ship-bar__msg free-ship-bar__msg--remaining';
            const template =
              this.dataset.remainingText || 'Add [amount] more for free shipping';
            msgEl.innerHTML = template.replace(
              '[amount]',
              `<strong>${this._formatMoney(remainingCents)}</strong>`
            );
          }
        }

        /* ── Icon swap ────────────────────────────────── */
        const iconEl = this.querySelector('.free-ship-bar__icon');
        if (iconEl) {
          iconEl.innerHTML = unlocked
            ? this._checkIcon()
            : this._truckIcon();
        }

        /* ── Host class ───────────────────────────────── */
        this.classList.toggle('free-ship-bar--unlocked', unlocked);
      }

      /* ── Money formatter ──────────────────────────────── */

      _formatMoney(cents) {
        const currency =
          (window.Shopify?.currency?.active) ||
          document.documentElement.getAttribute('data-currency') ||
          'USD';
        const locale = window.Shopify?.locale || document.documentElement.lang || 'en';

        try {
          return new Intl.NumberFormat(locale, {
            style: 'currency',
            currency,
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
          }).format(cents / 100);
        } catch {
          return `${(cents / 100).toFixed(2)} ${currency}`;
        }
      }

      /* ── Inline SVG icons ─────────────────────────────── */

      _truckIcon() {
        return `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16"
          viewBox="0 0 24 24" fill="none" stroke="currentColor"
          stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"
          aria-hidden="true" focusable="false">
          <rect x="1" y="3" width="15" height="13" rx="1"/>
          <polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/>
          <circle cx="5.5" cy="18.5" r="2.5"/>
          <circle cx="18.5" cy="18.5" r="2.5"/>
        </svg>`;
      }

      _checkIcon() {
        return `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16"
          viewBox="0 0 24 24" fill="none" stroke="currentColor"
          stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
          aria-hidden="true" focusable="false">
          <polyline points="20 6 9 17 4 12"/>
        </svg>`;
      }
    }
  );
}
